import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { DateTimeService } from '@y42/date-time';
import 'quill-mention';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  tap,
  timer,
} from 'rxjs';
import type { ChatRoomMessageUIModel, ChatRoomMessageUIPayload, MentionUser } from '../../models/chat-room.model';

@UntilDestroy()
@Component({
  selector: 'y42-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent {
  constructor(private readonly dateTimeService: DateTimeService) {
    this.highlight$
      .pipe(
        filter((val) => !!val),
        debounceTime(3000), // this value is correlated with anitmation time in scss file
        tap(() => this.highlight$.next(false)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  @Input()
  @InputObservable()
  message?: ChatRoomMessageUIModel;
  message$!: Observable<ChatRoomMessageUIModel | undefined>;

  private readonly highlight$ = new BehaviorSubject<boolean>(false);
  @Input() set highlight(value: boolean) {
    if (value) {
      this.highlight$.next(true);
    }
  }

  @Input() mentionUsers: MentionUser[] = [];

  @Output() edited = new EventEmitter<{
    messageId: ChatRoomMessageUIModel['id'];
    editedMessage: ChatRoomMessageUIPayload;
  }>();
  @Output() editPhase = new EventEmitter<'stared' | 'finished'>();
  @Output() deleted = new EventEmitter<ChatRoomMessageUIModel['id']>();

  private readonly isHover$ = new BehaviorSubject(false);
  private readonly mode$ = new BehaviorSubject<'view' | 'edit'>('view');
  private readonly editedContent$ = new BehaviorSubject<{ text: string; html: string } | null>(null);
  private readonly isControlsMenuOpened$ = new BehaviorSubject<boolean>(false);

  private readonly isEditable$ = this.message$.pipe(
    map((val) => !!val?.controls?.isEditable),
    distinctUntilChanged(),
  );

  private readonly isDeletable$ = this.message$.pipe(
    map((val) => !!val?.controls?.isDeletable),
    distinctUntilChanged(),
  );

  public readonly unknownUserTooltip = `The author's account couldn't be found. It may have been deprovisioned or deleted`;

  public readonly state = connectState(this, {
    avatar: this.message$.pipe(map((val) => val?.avatar || { full_name: '', imgSrc: null, isUnknown: true })),
    message: this.message$.pipe(
      tap(() => this.editedContent$.next(null)),
      map((val) => ({ text: val?.message || '', html: val?.messageHtml || '' })),
    ),
    messageTimesStats: combineLatest([
      timer(0, 1000).pipe(untilDestroyed(this)),
      this.message$.pipe(
        map((val) => ({ messageCreatedTime: val?.messageCreatedTime, messageUpdatedTime: val?.messageUpdatedTime })),
      ),
      this.editedContent$,
    ]).pipe(
      map(([__, messageTimes, editedContent]) => ({
        messageCreatedTime:
          messageTimes.messageCreatedTime && !editedContent
            ? this.dateTimeService.formatDateRelativeToNow(messageTimes.messageCreatedTime)
            : '',
        messageUpdatedTime:
          messageTimes.messageUpdatedTime && !editedContent
            ? this.dateTimeService.formatDateRelativeToNow(messageTimes.messageUpdatedTime)
            : '',
        isUpdatedDiffFromCreated:
          !!messageTimes.messageCreatedTime &&
          messageTimes.messageUpdatedTime &&
          messageTimes.messageCreatedTime.getTime() !== messageTimes.messageUpdatedTime.getTime(),
      })),
      startWith({
        messageCreatedTime: '',
        messageUpdatedTime: '',
        isUpdatedDiffFromCreated: false,
      }),
    ),
    isDeletable: this.isDeletable$,
    isEditable: this.isEditable$,
    showMenu: combineLatest([
      this.isHover$,
      this.isEditable$,
      this.isDeletable$,
      this.mode$,
      this.isControlsMenuOpened$,
    ]).pipe(
      map(([isHover, isEditable, isDeletable, mode, isControlsMenuOpened]) => {
        return (isHover && (isEditable || isDeletable) && mode === 'view') || isControlsMenuOpened;
      }),
      startWith(false),
      distinctUntilChanged(),
      debounceTime(0),
    ),
    mode: this.mode$,
    editedContent: this.editedContent$,
    highlight: this.highlight$,
  });

  setHover(isHover: boolean) {
    this.isHover$.next(isHover);
  }

  startEdit() {
    this.mode$.next('edit');
    this.isHover$.next(false);
    this.editPhase.emit('stared');
  }

  onCanceled() {
    this.mode$.next('view');
    this.isHover$.next(false);
    this.editPhase.emit('finished');
  }

  onCommentChanged(editedComment: ChatRoomMessageUIPayload) {
    this.mode$.next('view');
    this.isHover$.next(false);
    this.editedContent$.next({ text: editedComment.content, html: editedComment.contentHtml });
    this.edited.emit({ messageId: this.message!.id, editedMessage: editedComment });
    this.editPhase.emit('finished');
  }

  deleteMessage() {
    const messageId = this.message!.id;
    if (!messageId) {
      return;
    }
    this.deleted.emit(messageId);
  }

  switchIsControlsMenuOpened(val: boolean) {
    this.isControlsMenuOpened$.next(val);
  }
}
